import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import BigNumber from 'bignumber.js';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Grid, useMediaQuery } from '@material-ui/core';

import { ROUTE_DETAILS, ROUTE_OVERVIEW, ROUTE_STAKING, ROUTE_REWARDS } from 'constants/routes';

import { StakedIcon, UnclaimedRewardsIcon } from 'icons';

import { formatToken, toUSD, calcRainAPR, calcRaiUniAPR } from 'helpers/utils';

import Table from 'shared/components/Table';
import ButtonCell from 'shared/components/Table/components/ButtonCell';
import TypographyCell from 'shared/components/Table/components/TypographyCell';
import InfoCard from 'shared/components/Cards/Info';
import PriceCard from 'shared/components/Cards/Price';
import StatisticCard from 'shared/components/Cards/Statistic';
import MetaMaskDialog from 'shared/dialogs/MetaMask';

import { WalletActions } from 'store/reducers/wallet';

import { useStyles } from './styles';

import { PoolActions } from 'store/reducers/pool';
import clsx from 'clsx';
import { Decimal } from 'shared/components/Decimal';
import WithdrawDialog from 'shared/dialogs/Withdraw';
import { SERVICE_ADDRESS } from 'constants/address';
import { VestingActions } from 'store/reducers/vesting';

const scrollToRef = (ref: any) => window.scrollTo(0, ref.current.offsetTop);

const Home = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const myScrollRef = useRef<HTMLInputElement>(null);

  const {
    wallet: { address },
    metamask: { isMetaMask, loading: metamaskLoading },
  } = useSelector((state) => state);

  const rainEstimatedPrice = useSelector((state) => state.estimatedPrice.rainPrice);
  const lpEstimatedPrice = useSelector((state) => state.estimatedPrice.lpPrice);
  const LPLiquidity = useSelector((state) => state.pool.currentLPLiquidity);
  const fetchedData = useSelector((state) => state.pool.data);

  const [showMetaMask, setShowMetaMask] = useState<boolean>(false);
  const [accountWithdrawableRewards, setAccountWithdrawableRewards] = useState<string>('0');
  const [accountTotalDeposit, setAccountTotalDeposit] = useState<string>('0');
  const [allPoolsTotalDeposit, setAllPoolsTotalDeposit] = useState<string>('0');
  const [escrowPoolTotalDeposit, setEscrowPoolTotalDeposit] = useState<string>('0');

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [payload, setPayload] = useState<any>();

  const rewardTokenSymbol = fetchedData?.rewardTokenSymbol || 'RAIN';
  const isConnected = address !== '';
  const pools = fetchedData?.pools || [];
  const isMobile = useMediaQuery('(max-width:960px)');
  const deposits = ([] as any[]).concat(
    ...(fetchedData?.pools || [])
      //@ts-ignore
      .map((pool, index) =>
        pool.deposits.map((item, depositId) => {
          return {
            ...item,
            symbol: index !== 0 ? 'RAIN/BNB Pancakeswap LP' : 'RAIN',
            poolAddress: pool.poolAddress,
            depositId: depositId,
            depositTokenSymbol: pool.depositTokenSymbol,
          };
        }),
      ),
  );
  const uniLPAPR = calcRaiUniAPR(
    fetchedData?.rewardPerSecond || 0,
    (fetchedData ? +fetchedData.pools[1].weight : 0),
    rainEstimatedPrice,
    +pools[1]?.poolTotalDeposit || 0,
    lpEstimatedPrice,
  );
  const rainAPR = calcRainAPR(
    fetchedData?.pools[0].poolTotalDeposit || 0,
    fetchedData?.rewardPerSecond || 0,
    (fetchedData ? +fetchedData.pools[0].weight : 0),
  )


  useEffect(() => {
    if (!fetchedData) return;

    let totalWithdrawableRewardsTemp = new BigNumber(0);
    let accountTotalDepositsTemp = 0;
    let allPoolsTotalDepositTemp = 0;

    fetchedData.pools.forEach((pool, index) => {
      // eslint-disable-next-line max-len
      totalWithdrawableRewardsTemp = totalWithdrawableRewardsTemp.plus(pool.accountPendingRewards);
      accountTotalDepositsTemp +=
        +formatToken(pool.accountTotalDeposit) *
        (index === 0 ? rainEstimatedPrice : lpEstimatedPrice);
      allPoolsTotalDepositTemp +=
        index === 0
          ? +formatToken(pool.poolTotalDeposit) * rainEstimatedPrice
          : +formatToken(pool.poolTotalDeposit) * lpEstimatedPrice;
    });

    setAccountWithdrawableRewards(totalWithdrawableRewardsTemp.toFixed());
    setAccountTotalDeposit(accountTotalDepositsTemp.toFixed(2));
    setAllPoolsTotalDeposit(allPoolsTotalDepositTemp.toFixed(2));
    setEscrowPoolTotalDeposit(fetchedData.escrowPool.poolTotalDeposit);
  }, [fetchedData, rainEstimatedPrice, lpEstimatedPrice]);

  useEffect(() => {
    dispatch(PoolActions.poolsDataRequest({ account: address || SERVICE_ADDRESS }));
  }, []);

  const withdrawDeposit = (pool: string, depositId: string) => {
    setPayload({ pool, owner: address, depositId, receiver: address });
    dispatch(PoolActions.withdrawRequest({ pool, owner: address, depositId, receiver: address }));
    setOpenModal(true);
  };

  const scrollTo = () => scrollToRef(myScrollRef);

  const handleCloseMetaMask = () => {
    setShowMetaMask(false);
  };

  const handleOpenMetaMask = () => {
    setShowMetaMask(true);
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const handleButton = (error: boolean) => {
    if (error) {
      dispatch(PoolActions.withdrawRequest(payload));
    } else {
      closeModal();
    }
  };

  const handleMetaMaskButton = () => {
    if (!isMetaMask) {
      return window.open('https://metamask.io/download.html', '_blank');
    }

    dispatch(
      WalletActions.connectWallet({
        callback: () => {
          handleCloseMetaMask();
          history.push(ROUTE_OVERVIEW);
        },
      }),
    );
  };

  const handleTrustClick = () => {
    handleCloseMetaMask();

    dispatch(WalletActions.trustConnect());
  };

  const columnsPools = [
    {
      // @ts-ignore
      cell: ({ symbol }, index) => {
        return <TypographyCell variant="subtitle2">{index !== 0 ? 'RAIN/BNB Pancakeswap LP' : 'RAIN'}</TypographyCell>;
      },
      title: 'Core Pools',
    },
    {
      // @ts-ignore
      cell: ({ poolTotalDeposit }, index) => {
        if (index !== 0) {
          return (
            <TypographyCell variant="subtitle2">{`$${Decimal.format(
              +formatToken(poolTotalDeposit) * lpEstimatedPrice,
              2,
              ',',
            )}`}</TypographyCell>
          );
        }

        return (
          <TypographyCell variant="subtitle2">{`$${Decimal.format(
            toUSD(formatToken(poolTotalDeposit || '0')) *
              (index === 0 ? rainEstimatedPrice : lpEstimatedPrice),
            2,
            ',',
          )}`}</TypographyCell>
        );
      },
      title: 'Total Value Locked',
    },
    {
      // @ts-ignore
      cell: (data, index) => {
        if (index === 0) {
          return (
            <TypographyCell variant="subtitle2">
              {rainAPR}
            </TypographyCell>
          );
        }

        return (
          <TypographyCell variant="subtitle2">
            {uniLPAPR}
          </TypographyCell>
        );
      },
      title: 'APR',
    },
    {
      // @ts-ignore
      cell: ({ poolAddress }, index) => {
        return (
          <Box
            className={clsx({
              [classes.actions]: true,
              [classes.uniActions]: index === 1,
            })}
          >
            {index === 1 && (
              <ButtonCell
                variant="contained"
                color="secondary"
                onClick={() =>
                  window.open(
                    'https://pancakeswap.finance/add/0x6Bcd897D4BA5675F860C7418ddc034f6c5610114/BNB',
                  )
                }
              >
                Add LP
              </ButtonCell>
            )}
            <ButtonCell
              variant="contained"
              color="secondary"
              onClick={() => history.push(`${ROUTE_DETAILS}/${poolAddress}`)}
            >
              Details
            </ButtonCell>
            <ButtonCell
              variant="contained"
              onClick={() => history.push(`${ROUTE_STAKING}/${poolAddress}`)}
            >
              Stake
            </ButtonCell>
          </Box>
        );
      },
      title: '',
    },
  ];
  const depositColumns = [
    {
      // @ts-ignore
      cell: ({ symbol }) => {
        return <TypographyCell variant="subtitle2">{symbol}</TypographyCell>;
      },
      title: 'Pool',
    },
    {
      // @ts-ignore
      cell: ({ amount, depositTokenSymbol }) => {
        return (
          <TypographyCell variant="subtitle2">{`${+(+formatToken(+amount, {
            decimalPlaces: 18,
          })).toFixed(18)} ${depositTokenSymbol}`}</TypographyCell>
        );
      },
      title: 'Amount Staked',
    },
    {
      // @ts-ignore
      cell: ({ start }) => {
        return (
          <TypographyCell variant="subtitle2">{moment(+start * 1000).format('lll')}</TypographyCell>
        );
      },
      title: 'Lock Date',
    },
    {
      // @ts-ignore
      cell: ({ end }) => {
        return (
          <TypographyCell variant="subtitle2">{moment(+end * 1000).format('lll')}</TypographyCell>
        );
      },
      title: 'Unlock Date',
    },
    {
      // @ts-ignore
      cell: (data) => {
        const { poolAddress, depositId, end } = data;
        const pastTime = moment(new Date())
          .to(end * 1000)
          .includes('ago');

        return (
          <Box className={classes.actions}>
            <ButtonCell
              variant="contained"
              className={classes.btn}
              disabled={!pastTime}
              onClick={() => withdrawDeposit(poolAddress, depositId.toString())}
            >
              Withdraw
            </ButtonCell>
          </Box>
        );
      },
      title: '',
    },
  ];

  return (
    <div className={classes.wrapper}>
      {isMobile ? (
        <Box>
          <Box className={classes.contentWrapper}>
            <Box className={classes.bodyWrapper}>
              <Box className={classes.bodyContent}>
                <Box className={classes.title}>
                  <Typography className={classes.titleBlack}>Ready to make it</Typography>
                  <Typography className={classes.titlePink}>rain?</Typography>
                </Box>
                <Box className={classes.desc}>
                  <Typography>
                    Staking is live for $RAIN! We’re building the future of P2E games and connecting
                    the ecosystem of gamers, games, and guilds like never before. Start staking and
                    let’s make it rain for you. Learn more at{' '}
                    <a
                      className={classes.link}
                      href="https://rmg.io/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      rmg.io.
                    </a>
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Grid
            container
            direction={isMobile ? 'column' : 'row'}
            className={classes.blockCards}
            spacing={3}
          >
            <Grid item xs={12} md={6}>
              <Box mt={5}>
                <InfoCard
                  icon={() => <StakedIcon />}
                  title="Staked"
                  price={+accountTotalDeposit}
                  rain=""
                  btnText={isConnected ? 'Stake' : 'Connect wallet'}
                  btnClick={isConnected ? scrollTo : handleOpenMetaMask}
                />
                <Box mt={isMobile ? 3 : 5}>
                  <PriceCard title="Total Amount Staked" value={`$${Decimal.format(+allPoolsTotalDeposit, 2, ',')}`} />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <InfoCard
                title="Unclaimed Rewards"
                price={toUSD(formatToken(accountWithdrawableRewards, { decimalPlaces: 18 }))}
                icon={() => <UnclaimedRewardsIcon />}
                rain={`${rewardTokenSymbol} ${toUSD(
                  formatToken(accountWithdrawableRewards, { decimalPlaces: 2 }),
                )}`}
                btnText={isConnected ? 'Claim' : 'Connect wallet'}
                btnClick={isConnected ? () => history.push(ROUTE_REWARDS) : handleOpenMetaMask}
              />
              <Box mt={isMobile ? 3 : 5}>
                <PriceCard
                  title="Total Amount Claimed"
                  value={`$${Decimal.format(toUSD(formatToken(escrowPoolTotalDeposit, { decimalPlaces: 18 })), 2, ',')}`}
                />
              </Box>
                <Box mt={isMobile ? 3 : 5}>
                  <PriceCard title="RAIN APR" value={rainAPR} />
                </Box>
                <Box mt={isMobile ? 3 : 5}>
                  <PriceCard title="RAIN/BNB Pancakeswap LP APR" value={uniLPAPR} />
                </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box className={classes.content}>
          <Typography variant="h3" className={classes.watermark}>
            rainmakers games
          </Typography>
          <Box className={classes.contentWrapper}>
            <Box className={classes.bodyWrapper}>
              <Box className={classes.bodyContent}>
                <Box className={classes.title}>
                  <Typography className={classes.titleBlack}>Ready to make it</Typography>
                  <Typography className={classes.titlePink}>rain?</Typography>
                </Box>
                <Box className={classes.desc}>
                  <Typography>
                    Staking is live for $RAIN! We’re building the future of P2E games and connecting
                    the ecosystem of gamers, games, and guilds like never before. Start staking and
                    let’s make it rain for you. Learn more at{' '}
                    <a
                      className={classes.link}
                      href="https://rmg.io/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      rmg.io.
                    </a>
                  </Typography>
                </Box>
                {!isConnected && (
                  <Box>
                    <Typography className={classes.connectWallet} onClick={handleOpenMetaMask}>
                      Connect wallet
                    </Typography>
                  </Box>
                )}
              </Box>
              <Grid
                container
                spacing={5}
                direction={isMobile ? 'column' : 'row'}
                className={classes.blockCards}
              >
                <Grid item container className={classes.tokenInfoContainer}>
                  <Grid item md={6}>
                    <InfoCard
                      icon={() => <StakedIcon />}
                      title="Staked"
                      price={+accountTotalDeposit}
                      rain=""
                      btnText={isConnected ? 'Stake' : 'Connect wallet'}
                      btnClick={isConnected ? scrollTo : handleOpenMetaMask}
                    />
                  </Grid>
                  <Grid item md={6}>
                    <InfoCard
                      title="Unclaimed Rewards"
                      price={
                        toUSD(formatToken(accountWithdrawableRewards, { decimalPlaces: 18 })) *
                        rainEstimatedPrice
                      }
                      icon={() => <UnclaimedRewardsIcon />}
                      rain={`${rewardTokenSymbol} ${formatToken(accountWithdrawableRewards)}`}
                      btnText={isConnected ? 'Claim' : 'Connect wallet'}
                      btnClick={
                        isConnected ? () => history.push(ROUTE_REWARDS) : handleOpenMetaMask
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container item className={classes.tokenInfoContainer}>
                  <Grid item md={6}>
                    <PriceCard title="Total Amount Staked" value={`$${Decimal.format(+allPoolsTotalDeposit, 2, ',')}`} />
                  </Grid>
                  <Grid item md={6}>
                    <PriceCard
                      title="Total Amount Claimed"
                      value={
                        `$${Decimal.format(toUSD(formatToken(escrowPoolTotalDeposit, { decimalPlaces: 18 })) *
                        rainEstimatedPrice, 2, ',')}`
                      }
                    />
                  </Grid>
                </Grid>
                <Grid container item className={classes.tokenInfoContainer}>
                  <Grid item md={6}>
                    <PriceCard title="RAIN APR" value={rainAPR} />
                  </Grid>
                  <Grid item md={6}>
                    <PriceCard
                      title="RAIN/BNB Pancakeswap LP APR"
                      value={uniLPAPR}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      )}
      {isConnected && (
        <Box className={classes.blockStatistic}>
          {isMobile ? (
            <Box>
              {pools?.length !== 0 && (
                <Typography variant="h2" ref={myScrollRef}>
                  Pools
                </Typography>
              )}
              {pools?.map((pool, index) => {
                return (
                  <StatisticCard
                    type="pool"
                    index={index}
                    key={pool.symbol}
                    rewardPerSecond={fetchedData?.rewardPerSecond}
                    statistic={{ ...pool, symbol: index === 0 ? 'RAIN' : 'RAIN/BNB Pancakeswap LP' }}
                  />
                );
              })}
            </Box>
          ) : (
            <Box mb={isMobile ? 3 : 5}>
              <Typography variant="h2" ref={myScrollRef}>
                Pools
              </Typography>
              <Box mt={4}>
                <Table<any> data={pools || []} loading={false} columns={columnsPools} />
              </Box>
            </Box>
          )}

          {isMobile ? (
            <Box>
              {deposits.length !== 0 && <Typography variant="h2">Deposits</Typography>}
              {deposits.map((deposit, index) => {
                return (
                  <StatisticCard
                    type="deposit"
                    index={index}
                    key={deposit.symbol}
                    statistic={deposit}
                    address={address}
                  />
                );
              })}
            </Box>
          ) : (
            <Box mt={isMobile ? 5 : 10}>
              <Typography variant="h2">Deposits</Typography>
              <Box mt={4}>
                <Table<any> data={deposits} loading={false} columns={depositColumns} />
              </Box>
            </Box>
          )}
        </Box>
      )}
      <MetaMaskDialog
        open={showMetaMask}
        isMetaMask={isMetaMask}
        handleClose={handleCloseMetaMask}
        loading={metamaskLoading}
        handleButton={handleMetaMaskButton}
        handleTrustClick={handleTrustClick}
      />
      <WithdrawDialog open={openModal} handleButton={handleButton} handleClose={closeModal} />
    </div>
  );
};

export default Home;
